import { keyBy, sortBy, uniqBy } from 'lodash';
import React from 'react';
import BoxScore from '~/src/components/BoxScore';
import LoadingBoundary from '~/src/components/LoadingBoundary';
import { Urls } from '~/src/lib/enums/urls';
import { OteGame } from '~/src/models/OteGame';
import { OteGameOtePlayerOteTeam } from '~/src/models/OteGameOtePlayerOteTeam';
import { OteGameOteTeam } from '~/src/models/OteGameOteTeam';
import { OtePlayer } from '~/src/models/OtePlayer';
import { OteTeam } from '~/src/models/OteTeam';
import { useFetchAndSubscribe } from '~/src/overtime-lib/src/www/hooks/useFetch';
import './Players.scss';

const _Players = ({
	game,
	gameTeam,
	teamId,
	players,
}: {
	game: OteGame;
	gameTeam: OteGameOteTeam;
	teamId: string;
	players: OteGameOtePlayerOteTeam[];
}) => {
	const team = useFetchAndSubscribe<OteTeam>({
		url: `${Urls.ApiRestOteTeamsUrl}/${teamId}`,
		key: 'ote_team',
	});

	const stubPlayers: Record<string, OteGameOtePlayerOteTeam> = keyBy(
		team?.ote_players.map((ote_player) => ({
			id: ote_player.id,
			created_at: ote_player.created_at,
			updated_at: ote_player.updated_at,
			ote_player_id: ote_player.id,
			ote_player,
			ote_team_id: team.id,
			ote_game_id: game.id,
		})),
		'id',
	);

	const initialGamePlayers = keyBy(
		players?.filter(Boolean).filter((p) => p?.ote_team_id === teamId && p.ote_game_id === game.id),
		'ote_player_id',
	);

	const gamePlayers = {
		...stubPlayers,
		...initialGamePlayers,
	};

	const filteredPlayers: OtePlayer[] = sortBy(
		uniqBy(
			[...(team?.ote_players ?? []), ...Object.values(gamePlayers).map((g) => g.ote_player)].filter((p) =>
				['finished', 'confirmed'].includes(game.status) ? gamePlayers[p?.id]?.minutes > 0 : true,
			),
			'id',
		),
		['family_name', 'first_name'],
	);

	return (
		<BoxScore
			rows={[
				...filteredPlayers.map((p) => ({
					...gamePlayers[p?.id],
					ote_player: { ...p, ote_team: team },
					...p,
				})),
				gameTeam,
			]}
			isTakeover={game.game_category === 'Takeover'}
			title="Player"
			collapsableRowHeader={true}
			showStatRank={false}
			defaultHeader="minutes"
		/>
	);
};

const Players = ({
	ote_game,
	gameTeam,
	teamId,
	players,
}: {
	ote_game: OteGame;
	gameTeam: OteGameOteTeam;
	teamId: string;
	players: OteGameOtePlayerOteTeam[];
}) => {
	return (
		<div className="GamePlayers">
			<LoadingBoundary>
				<_Players players={players} game={ote_game} gameTeam={gameTeam} teamId={teamId} />
			</LoadingBoundary>
		</div>
	);
};

export default Players;
